import { action, observable } from 'mobx';

import ChatService from 'services/ChatService';
import GiphyService from 'services/GiphyService';
import messagingService from 'services/MessagingService/MessagingService';
import {
  GET_GIFS_RESPONSE,
  GET_GIFS_RESPONSE_STATUS,
} from 'services/MessagingService/EventNames';
import APIStates from 'enum/APIStates';

/**
 * @class handles state of aplication for giphy gifs and expose methods
 */
class GiphyStore {
  constructor(messagingService) {
    this.messagingService = messagingService();
    this.messagingService.on(GET_GIFS_RESPONSE, this.setGifResponse);
    this.messagingService.on(GET_GIFS_RESPONSE_STATUS, this.setGifResponseStatus);
  }

  destructor() {
    this.messagingService.off(GET_GIFS_RESPONSE, this.setGifResponse);
    this.messagingService.off(GET_GIFS_RESPONSE_STATUS, this.setGifResponseStatus);
  }

  @observable gifs = [];
  @observable isGifsWrapperVisible = false;
  @observable anchorElForGif = null;
  @observable gifResponseStatus = APIStates.INITIAL;
  @observable gifOffset = 0;
  @observable isLoadMoreLoading = false;
  @observable isDrawerOpened = false;

  /**
   * Method for fetching trending gifs
   * @param {number} offset - first value offset for fetching endpoint
   * @param {isLoadMore} bool - second value for showing the loading spinner
   */

  getTrendingGifs = (offset, isLoadMore) => {
    GiphyService.getTrendingGifs(offset, isLoadMore);
  };

  /**
   * Method for toggling mobile bottom drawer for gifs
   */

  toggleMobileDrawer = () => {
    this.isDrawerOpened = !this.isDrawerOpened;
  };

  /**
   * Method for closing mobile drawer for gifs(required prop for bottomDrawer component)
   */

  closeDrawer = () => {
    this.isDrawerOpened = false;
    this.gifs = [];
    this.setGifOffset(0);
  };

  /**
   * Method for fetching search gifs gifs
   * @param {string} searchValue - first value searchValue for fetching endpoint
   * @param {number} offset - second value offset for fetching endpoint
   * @param {isLoadMore} bool - third value for showing the loading spinner
   */

  getSearchGifs = (searchValue, offset, isLoadMore) => {
    GiphyService.getSearchGifs(searchValue, offset, isLoadMore);
  };

  /**
   * Method for setting observable for trending/search gifs
   * @param {number} offset - first value offset for fetching endpoint
   * @param {isLoadMore} bool - second value for showing the loading spinner
   */

  @action setGifResponse = ({ gifs, isLoadMore }) => {
    if (isLoadMore) {
      this.setIsLoadMoreLoading(false);
      this.gifs = [...this.gifs, ...gifs];
    } else {
      this.gifs = gifs;
    }
  };

  /**
   * Method for setting load more spinner for gifs
   */

  @action setIsLoadMoreLoading = (loading) => {
    this.isLoadMoreLoading = loading;
  };

  /**
   * Method for setting gif offset
   * @param {number} offset - first value offset for fetching endpoint
   */

  @action setGifOffset = (offset) => {
    if (Number.isInteger(offset)) {
      this.gifOffset = offset;
    } else {
      this.gifOffset += 20;
    }
  };

  /**
   * Method for setting status of trending/search gifs endpoint
   * @param {string} value - APIstates(PENDING,LOADED,ERROR)
   */

  @action setGifResponseStatus = (value) => {
    this.gifResponseStatus = value;
  };

  /**
   * Method for toggling desktop popper component for gifs
   * @param {event} event - first value event for fetching endpoint
   */

  @action openGifsAndSetAnchor = (event) => {
    this.anchorElForGif = this.anchorElForGif ? null : event.currentTarget;
    this.isGifsWrapperVisible = Boolean(this.anchorElForGif);
    if (!this.isGifsWrapperVisible) {
      this.gifs = [];
      this.setGifOffset(0);
    }
  };

  /**
   * Method for closing desktop popper component for gifs
   */

  @action closePopper = () => {
    this.anchorElForGif = null;
    this.isGifsWrapperVisible = false;
    this.gifs = [];
    this.setGifOffset(0);
  };

  @action async getSingleGif(message, messageKey, isLiveTouchOn, isLightMessage) {
    const result = await GiphyService.getSingleGif(message.gid);
    const gifUrls = {
      fixedWidth: result.data.images.fixed_width.webp,
      height: result.data.images.fixed_width.height,
      width: result.data.images.fixed_width.width,
    };
    const parsedMessage = isLightMessage ? ChatService.parseLightMessage(
      { ...message, isLiveTouchOn, gifUrls }
    ) : ChatService.parseHistoryMessage(
      messageKey, { ...message, isLiveTouchOn, gifUrls }
    );

    return parsedMessage;
  }
}

export const GiphyStoreClass = GiphyStore;

export default new GiphyStore(messagingService);
